import { Divider, Stack, Typography } from '@mui/material';
import { ReactElement } from 'react';

import { CheckCircleIcon } from '@/components/icons/CheckCircleIcon';
import { ShieldTickFilledIcon } from '@/components/icons/ShieldTickFilledIcon';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { COLORS } from '@/styles/tokens/colors';

import { HouseholdChatPersonaOption } from '../hooks/useSelectedHouseholdChatPersona';

interface HouseholdChatPersonaHoverCardProps {
  personaOption: HouseholdChatPersonaOption;
  /** The hover target, e.g. the persona's row in the selector menu. */
  children: ReactElement;
}

/**
 * The card body: the persona's identity (icon, display name, one-sentence
 * description) above a "This agent will" rundown of its personaFeatures.
 */
function PersonaCardBody({
  personaOption,
}: {
  personaOption: HouseholdChatPersonaOption;
}) {
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <ShieldTickFilledIcon
          aria-hidden
          size={24}
          color={COLORS.BRAND.ORANGE}
        />
        <Stack spacing={0.5}>
          <Typography variant="title-xs">
            {personaOption.displayName}
          </Typography>
          <Typography variant="body-xs" sx={{ color: COLORS.TEXT.PRIMARY }}>
            {personaOption.description}
          </Typography>
        </Stack>
      </Stack>
      <Divider sx={{ borderColor: COLORS.GRAY[200] }} />
      <Typography variant="eyebrow-xs">This agent will</Typography>
      {personaOption.personaFeatures.map((feature) => (
        <Stack
          key={feature}
          direction="row"
          spacing={1}
          alignItems="flex-start"
        >
          <CheckCircleIcon aria-hidden size={16} color={COLORS.TEAL[500]} />
          <Typography variant="body-xs" sx={{ color: COLORS.TEXT.PRIMARY }}>
            {feature}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * Wraps a persona option in the selector menu with a rich hover card
 * explaining what the persona (agent) does: its description plus a
 * checklist of its features.
 *
 * Implemented as a restyled Tooltip (white card instead of the default dark
 * bubble) so hover/focus/touch behavior and portal positioning come for
 * free. Opens to the left of the menu row because the selector sits against
 * the right edge of the chat panel.
 */
export function HouseholdChatPersonaHoverCard({
  personaOption,
  children,
}: HouseholdChatPersonaHoverCardProps) {
  return (
    <Tooltip
      // Pin the card's bottom edge to the menu row so it grows upward.
      placement="left-end"
      arrow={false}
      // Informational only: let the card dismiss as soon as the pointer
      // leaves the menu row instead of lingering while hovered.
      disableInteractive
      enterDelay={300}
      title={<PersonaCardBody personaOption={personaOption} />}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: { offset: [0, 4] },
            },
          ],
        },
        tooltip: {
          sx: {
            bgcolor: COLORS.PRIMITIVES.WHITE,
            border: `1px solid ${COLORS.GRAY[200]}`,
            borderRadius: '8px',
            boxShadow:
              '0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px rgba(17, 24, 39, 0.16)',
            width: 330,
            maxWidth: 'none',
            m: 0,
            p: 1.5,
          },
        },
      }}
    >
      {children}
    </Tooltip>
  );
}
